import React from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { openCalendly } from '../lib/calendly';

const scrollToSection = (href) => {
  const element = document.querySelector(href);
  if (element) element.scrollIntoView({ behavior: 'smooth' });
};

const headlineWords = ['Elite', 'Virtual', 'Assistants', 'On', 'Demand'];

export default function Hero() {
  const { scrollY } = useScroll();
  const bgTextX = useTransform(scrollY, [0, 800], [0, -300]);
  const contentY = useTransform(scrollY, [0, 500], [0, 120]);
  const contentOpacity = useTransform(scrollY, [0, 400], [1, 0]);

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden bg-[#050A08] grain-overlay" data-testid="hero-section">
      {/* Radial emerald glow */}
      <div className="absolute top-0 left-1/4 w-[800px] h-[800px] bg-emerald-500/20 rounded-full blur-[160px] pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-teal-500/10 rounded-full blur-[140px] pointer-events-none" />

      {/* Giant kinetic background wordmark */}
      <motion.div
        style={{ x: bgTextX }}
        className="absolute top-1/2 -translate-y-1/2 left-0 whitespace-nowrap pointer-events-none select-none z-0"
        aria-hidden="true"
      >
        <span className="font-display font-black text-[24vw] leading-none text-white/[0.02] tracking-tighter">
          OUTSOURCEDHUB
        </span>
      </motion.div>

      {/* Dot grid */}
      <div
        className="absolute inset-0 opacity-40 z-0"
        style={{
          backgroundImage: 'radial-gradient(circle, rgba(16,185,129,0.12) 1px, transparent 1px)',
          backgroundSize: '38px 38px',
          maskImage: 'radial-gradient(ellipse 70% 60% at 50% 40%, #000 60%, transparent 100%)',
        }}
      />

      <motion.div
        style={{ y: contentY, opacity: contentOpacity }}
        className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 w-full pt-32 pb-20"
      >
        {/* Eyebrow */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/25 mb-8"
          data-testid="hero-badge"
        >
          <Sparkles className="w-4 h-4 text-emerald-400" />
          <span className="text-sm text-emerald-100/90 font-medium tracking-wide">
            Trusted by 500+ growing businesses worldwide
          </span>
        </motion.div>

        {/* Kinetic headline */}
        <h1 className="font-display font-black text-white leading-[0.92] tracking-tighter text-5xl sm:text-7xl lg:text-8xl max-w-5xl mb-8">
          {headlineWords.map((word, i) => (
            <span key={i} className="inline-block overflow-hidden mr-[0.25em] align-top">
              <motion.span
                initial={{ y: '110%' }}
                animate={{ y: 0 }}
                transition={{ duration: 0.8, delay: 0.15 + i * 0.09, ease: [0.22, 1, 0.36, 1] }}
                className={`inline-block ${word === 'Virtual' || word === 'Assistants' ? 'text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 via-teal-400 to-lime-400' : ''}`}
              >
                {word}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.7 }}
          className="text-lg md:text-xl text-slate-400 max-w-2xl mb-12 leading-relaxed"
        >
          We handpick skilled virtual assistants matched to your workflow, culture, and goals — so you can reclaim your time and scale with total confidence.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.85 }}
          className="flex flex-col sm:flex-row items-start gap-4"
        >
          <Button
            onClick={openCalendly}
            data-testid="hero-primary-cta"
            className="group bg-emerald-500 hover:bg-emerald-400 text-[#04120C] px-8 py-6 text-base rounded-full font-bold transition-all duration-300 hover:shadow-[0_0_40px_rgba(16,185,129,0.5)]"
          >
            Book a Free Consultation
            <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
          </Button>
          <Button
            onClick={() => scrollToSection('#services')}
            variant="outline"
            data-testid="hero-secondary-cta"
            className="border border-white/15 bg-white/[0.02] text-white hover:bg-white/[0.06] hover:border-emerald-500/40 px-8 py-6 text-base rounded-full font-semibold backdrop-blur-sm transition-all duration-300"
          >
            Explore Our Services
          </Button>
        </motion.div>

        {/* Stats strip */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1 }}
          className="mt-20 flex flex-wrap gap-x-16 gap-y-8 border-t border-white/[0.06] pt-10"
        >
          {[
            { label: 'Active Clients', value: '500+' },
            { label: 'Hours Saved', value: '100K+' },
            { label: 'Satisfaction', value: '98%' },
            { label: 'Countries', value: '25+' },
          ].map((stat, index) => (
            <div key={stat.label} data-testid={`hero-stat-${index}`}>
              <div className="font-display text-4xl md:text-5xl font-black text-white mb-1">
                {stat.value}
              </div>
              <div className="text-sm text-slate-500 uppercase tracking-wider">{stat.label}</div>
            </div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
}
